import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Card, CardBody, CardTitle, CardText, Spinner } from "reactstrap"
import jwt from "jsonwebtoken"

import subdomains from "../constants/subdomains.json"
import blankLoadingPic from "../assets/images/blank-loading.png"
import idexoLogo from "../assets/images/logo-dark.png"
import { KNOWN_CHAINS } from "../libs/useMetaMaskWallet/utils"
import { useWallet } from "../libs/useMetaMaskWallet"
import LoadingOrErrorCard from "../Components/LoadingOrErrorCard/LoadingOrErrorCard"
import ConfirmSubmitModal from "../Components/ConfirmSubmitModal/ConfirmSubmitModal"
import ConnectWallet from "../Components/ConnectWallet"
import BreadCrumb from "../Components/BreadCrumb"
import FractalKYC from "../Components/FractalKYC/FractalKYC"
import { networkIcons, flattenTokenGatingNFTs } from "../utils"

const apiUrlTokenGating = "https://gating.idexo.io"

const parts = window.location.hostname.split(".") // ['swordsofblood', 'idexo', 'com'], ['localhost'], or ['app', 'idexo', 'io']
const subdomain = parts[0] // 'swordsofblood', 'localhost', 'app'
// const subdomain = "swordsofblood"
// const subdomain = "bionicowls"
// const subdomain = "app"
// NOTE: for testing dynamic text rendering, change the subdomain value above

const TokenGating = () => {
    const wallet = useWallet()
    const [isConnectedToWallet, setIsConnectedToWallet] = useState(wallet?.status === "connected")
    const [userNFTs, setUserNFTs] = useState(null) // [{ chain:'bnbchain', tokens:[] }, { chain:'polygon', tokens:[]}, {}, {}, ...]
    const [showLoadingNFTs, setShowLoadingNFTs] = useState(false) // spinner animation for fetching user community nfts
    const [errorMessage, setErrorMessage] = useState("") // error alert fetching user community nfts
    const [fractalValidationStatus, setFractalValidationStatus] = useState("Not Validated") // Validated if user has 1) any idexo staking NFTs or 2) idexo Early Adopter NFTs
    const [headerText, setHeaderText] = useState(subdomains[subdomain]?.tokenGatingHeaderText)

    // modal for user to link their email to wallet address in crm
    const [showLinkEmailModal, setShowLinkEmailModal] = useState(false) // modal for user to confirm linking their email with the associated wallet address (self custody) in the crm DB
    const [modalAlertMessage, setModalAlertMessage] = useState("")
    const [modalAlertColor, setModalAlertColor] = useState("")
    const [disableModalButtons, setDisableModalButtons] = useState(false)
    const [showConfirmLoading, setShowConfirmLoading] = useState(false) // loading animation after clicking "confirm" button
    const [tenantId, setTenantId] = useState("") // tenantId of subdomain that launched the twitter campaign/conversationID
    const [crmId, setCRMid] = useState("")

    const decoded = jwt.decode(localStorage.jwtToken)

    // links email to wallet address (self custody address) in crm DB
    const confirmEmail = async () => {
        setShowConfirmLoading(true)
        setModalAlertMessage("")
        try {
            const res = await fetch(apiUrlTokenGating + `?path=crm&address=${wallet.account.toLowerCase()}`, {
                method: "POST",
                headers: { token: localStorage.jwtToken },
                body: JSON.stringify({
                    email: decoded.email.toLowerCase(),
                    tenantId: tenantId, // tenantId of subdomain that launched the twitter campaign/conversationID
                    id: crmId
                })
            })
            const response = await res.json()
            console.log("confirmEmail", response)

            if (response.error || response.code !== 200) {
                setShowConfirmLoading(false)
                setModalAlertColor("danger")
                setModalAlertMessage(response.error)
                return
            }
            setModalAlertColor("success")
            setModalAlertMessage("successfully linked email! Returning to token gating page...")
            setDisableModalButtons(true)
            setTimeout(() => {
                setShowLinkEmailModal(false)
                setDisableModalButtons(false)
                setModalAlertMessage("")
            }, 3000)
        } catch (err) {
            console.log(err)
            setModalAlertColor("danger")
            setModalAlertMessage(err)
        }
        setShowConfirmLoading(false)
    }

    // checks if email is associated with a wallet_address (self custody) in the crm DB
    const checkEmailHasWalletAddress = async () => {
        try {
            const res = await fetch(apiUrlTokenGating + `?path=crm&address=${wallet.account.toLowerCase()}`, {
                method: "GET",
                headers: { token: localStorage.jwtToken }
            })
            const response = await res.json()
            console.log("checkEmailHasWalletAddress", response)

            // only show confirm email modal if:
            // - there's an item in the crm DB with the wallet_address = users connected metamask address
            // - the user has not already linked their email address
            const crmItem = response?.data?.Items?.[0]
            if (!response.error && crmItem && crmItem?.email === undefined) {
                setShowLinkEmailModal(true)
                setTenantId(crmItem.tenant_id.S)
                setCRMid(crmItem.id.S)
            }
        } catch (err) {
            console.log(err)
        }
    }

    // app.idexo.io/tokengating DEMO
    const fetchCommunityAndStakingNFTs = async () => {
        // const testAddresses = ["0xaF6a74D1fb9087bf9155a1a0EfB8735a3f360F6D", "0x3ca28E87e34b95Dbd306CcDab2e84De9B56f248E"]
        const [communityNFTsRes, stakingNFTsRes] = await Promise.all(
            ["nfts", "stakes"].map(async (path, idx) => {
                return fetch(apiUrlTokenGating + `?path=${path}&account=${wallet.account.toLowerCase()}`, {
                    // return fetch(apiUrlTokenGating + `?path=${path}&account=${testAddresses[idx]}`, {
                    method: "GET",
                    headers: { token: localStorage.jwtToken }
                }).then((res) => res.json())
            })
        )
        // console.log("communityNFTsRes", communityNFTsRes)
        // console.log("stakingNFTsRes", stakingNFTsRes)

        // exit if any responses have errors
        if ([communityNFTsRes, stakingNFTsRes].some((res) => res.error || res.code !== 200)) {
            setErrorMessage(communityNFTsRes.error || stakingNFTsRes.error)
            return setShowLoadingNFTs(false)
        }

        // flattening community and staking responses nested data to be more readable/loopable
        const flattenedCommunityNFTs = flattenTokenGatingNFTs(communityNFTsRes.data)
        const flattenedStakingNFTs = flattenTokenGatingNFTs(stakingNFTsRes.data)

        setUserNFTs(flattenedCommunityNFTs.concat(flattenedStakingNFTs))
    }

    // ex. for fetching swords of blood users nfts
    const fetchSubdomainUserNFTs = async (subdomain) => {
        // FOR TESTING
        // const sobHolderAddress = "0x3448a183da142bc6eccfdc690572dfe2d276ddfa"
        // const res = await fetch(apiUrlTokenGating + `?path=subdomains&address=${sobHolderAddress}&subdomainname=${subdomain}`, {
        const res = await fetch(apiUrlTokenGating + `?path=subdomains&address=${wallet.account.toLowerCase()}&subdomainname=${subdomain}`, {
            method: "GET",
            headers: { token: localStorage.jwtToken }
        })
        const response = await res.json()
        console.log("fetchSubdomainUserNFTs", response)

        if (response.code !== 200 || response.error || response?.data?.error) {
            return setErrorMessage(response.error || response?.data?.error)
        }

        setUserNFTs(response?.data?.tokenList || [])
    }

    const fetchUserNFTs = async (subdomain) => {
        setShowLoadingNFTs(true)
        setErrorMessage("")
        try {
            if (subdomain === "app" || subdomain === "localhost") {
                await fetchCommunityAndStakingNFTs()
            } else {
                await fetchSubdomainUserNFTs(subdomain)
            }
        } catch (err) {
            console.log(String(err))
            setErrorMessage(String(err))
        }
        setShowLoadingNFTs(false)
    }

    const updateConnection = () => {
        const x = wallet.status === "connected"
        setIsConnectedToWallet(x)
        return x
    }

    useEffect(async () => {
        if (updateConnection() && wallet.account) {
            fetchUserNFTs(subdomain)
            checkEmailHasWalletAddress()
        }
    }, [wallet.account, wallet.status, wallet.chainId])

    useEffect(async () => {
        if (
            fractalValidationStatus === "Not Validated" &&
            ((subdomain === "app" && userNFTs?.some((token) => token.poolName || token.name === "idexo Early Adopter NFTs")) ||
                (subdomain !== "app" && userNFTs?.length))
        ) {
            // a user is validated if they are token gated (ex. a user is a holder of SOB early adopter nft)
            setFractalValidationStatus("Validated")
        }
    }, [userNFTs])

    return (
        <div className="content token-gating">
            {/* OLD BreadCrumb */}
            {/* <BreadCrumb current="Token Gating" /> */}
            <BreadCrumb items={["Token Gating"]} />

            {!isConnectedToWallet ? (
                <Card className="token-gating-welcome">
                    <CardBody>
                        <CardTitle className="h3">{headerText}</CardTitle>
                        <CardText>Connect your wallet and your preferred chain to access gated content.</CardText>
                        <CardBody>
                            <ConnectWallet wallet={wallet} onChange={updateConnection} isTokenGating={true} />
                        </CardBody>
                    </CardBody>
                </Card>
            ) : (
                <React.Fragment>
                    <Card className="token-gating-welcome">
                        <CardBody>
                            <CardTitle className="h3">{headerText}</CardTitle>
                            {showLoadingNFTs ? (
                                // text that shows when card is loading
                                <LoadingOrErrorCard loadingTxt={subdomains[subdomain]?.tokenGatingLoadingTxt} className="token-gating-loading" />
                            ) : (
                                <React.Fragment>
                                    {errorMessage && (
                                        <LoadingOrErrorCard
                                            // text that shows if there's an error checking the user's nft's
                                            errorTxt={subdomains[subdomain]?.tokenGatingErrorTxt}
                                            alertMessage={errorMessage}
                                            className="token-gating-loading"
                                        />
                                    )}

                                    {!userNFTs?.length && !errorMessage && (
                                        // text that shows if the user has no token gated NFTs
                                        <LoadingOrErrorCard
                                            emptyTxt={subdomains[subdomain]?.tokenGatingEmptyTxt}
                                            emptyTxt2={""}
                                            className="token-gating-loading"
                                        />
                                    )}

                                    {userNFTs?.length >= 1 && !errorMessage && (
                                        <React.Fragment>
                                            <div className="welcome-message">
                                                {/* Below are your Community NFTs as well as your Staking NFTs. Click on an NFT to view the token details,
                                                or see token gated content such as reserving an Early Adopter v2 Soulbound Token (SBT). */}
                                                <div className={subdomain}>{subdomains[subdomain]?.tokenGatingWelcomeTextParagraph1}</div>
                                                <div className={subdomain === "app" ? "hide" : "show"}>
                                                    {subdomains[subdomain]?.tokenGatingWelcomeTextParagraph2}
                                                </div>
                                            </div>

                                            <div className="token-gating-card-wrap">
                                                {/* <CardText>Connected to {KNOWN_CHAINS[wallet.chainId]}</CardText> */}

                                                {userNFTs?.map((token, idx) => {
                                                    return (
                                                        <TokenGatingCard
                                                            userNFTs={userNFTs}
                                                            token={token}
                                                            wallet={wallet}
                                                            key={idx}
                                                            subdomain={subdomain}
                                                        />
                                                    )
                                                })}
                                            </div>
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            )}
                        </CardBody>
                    </Card>

                    {/* Show FractalKYC Component if user has 1) any idexo staking NFTs or 2) idexo Early Adopter NFTs or subdomain collection holder (ex. swordsofblood)*/}
                    {/* a token is a staking NFT if the token has an attribute ".poolName" */}
                    {fractalValidationStatus === "Validated" && <FractalKYC validationStatus={fractalValidationStatus} />}

                    {showLinkEmailModal && (
                        <ConfirmSubmitModal
                            showConfirmSubmitModal={true}
                            setShowConfirmSubmitModal={setShowLinkEmailModal}
                            modalAlertMessage={modalAlertMessage}
                            setModalAlertMessage={setModalAlertMessage}
                            alertColor={modalAlertColor}
                            setAlertColor={setModalAlertColor}
                            disableAllButtons={disableModalButtons}
                            showConfirmLoading={showConfirmLoading}
                            handleClickConfirm={confirmEmail}
                            modalTitle={"Confirm Email Address"}
                            confirmButtonText={"CONFIRM"}
                            confirmButtonLoading={"CONFIRMING"}
                        >
                            Would you like to link your email address, <span className="link-email">{decoded.email}</span>, with your wallet address?
                        </ConfirmSubmitModal>
                    )}
                </React.Fragment>
            )}
        </div>
    )
}

export default TokenGating

// token == {
//     chain: "polygon",
//     key: "ipcn",
//     name: "Idexo Polygon Community NFT",
//     symbol: "IPCN",
//     contract: "0xbA94192EEfe9b7A894D03C209Fc8292bd53dFDC2",
//     tokenId: "6",
//     owner: "0xaF6a74D1fb9087bf9155a1a0EfB8735a3f360F6D",
//     tokenURI: "https://arweave.net/_z7rs-am4osJndj7xiFuOwuWOgMO-WylpYl-sJG0DKA", // NOTE* stakingNFTs will have empty string "" tokenURI
// }

const TokenGatingCard = ({ userNFTs, token, subdomain, wallet }) => {
    const [showImageLoading, setShowImageLoading] = useState(true)
    const [tokenImgUrl, setTokenImgUrl] = useState(token.image || "")
    const [tokenMetadata, setTokenMetadata] = useState(null) // object- JSON
    const [errorFetchingMetadata, setErrorFetchingMetadata] = useState(false)

    const fetchTokenMetadata = async () => {
        try {
            let metadata_uri = token.tokenURI || token.uri
            metadata_uri = metadata_uri.replace("ipfs://", "https://nftstorage.link/ipfs/")
            const res = await fetch(metadata_uri, {
                method: "GET"
            })
            const response = await res.json()
            console.log("fetchTokenMetadata", response)

            if (res.status !== 200) {
                return setErrorFetchingMetadata(true)
            }
            let imageUri = response.uri || response.image || ""
            imageUri = imageUri.replace("ipfs://", "https://nftstorage.link/ipfs/")
            setTokenImgUrl(imageUri)
            setTokenMetadata(response)
        } catch (err) {
            console.log(err)
            setErrorFetchingMetadata(true)
        }
    }

    useEffect(async () => {
        if ((!token.tokenURI && subdomain === "app") || (!token.uri && subdomain !== "app") || tokenMetadata || errorFetchingMetadata) return
        await fetchTokenMetadata()
    }, [tokenImgUrl])

    return (
        // if user clicks TokenGatingCard component, render the TokenGatingDetail component (and use "fake" nested route '/tokengating/:token_address')
        <Link
            className="token-gating-card-link"
            // if token.id == true that means this token is the subdomain SOB token
            to={{
                pathname: `/tokengating/${token.address || token.contract || token.id}`,
                state: { token: token, metadata: tokenMetadata, subdomain }
            }}
        >
            <div className="token-gating-card">
                <div className={"image-wrap"}>
                    {/* show loading animation only if token has a tokenURI- stakingNFTs will have empty string "" tokenURI */}
                    {showImageLoading && (token.tokenURI || token.uri) && (
                        <div className="image-loading-wrap">
                            <img src={blankLoadingPic} alt="loading" />
                            <Spinner className="image-loading" />
                        </div>
                    )}

                    {/* if token is a community nft */}
                    {!token.poolName && subdomain === "app" && (
                        <img
                            src={tokenImgUrl || ""}
                            onLoad={() => setShowImageLoading(false)}
                            alt="collection name"
                            className={"token-img" + (showImageLoading ? " hide" : "")}
                        />
                    )}

                    {/* if token is a staking nft */}
                    {token.poolName && (
                        <img
                            src={idexoLogo}
                            onLoad={() => setShowImageLoading(false)}
                            className={"token-img" + (token.poolName ? " staking" : " community")}
                        />
                    )}

                    {/* if token is swords of blood early adopter nft */}
                    {subdomain === "swordsofblood" && (
                        <img
                            src={tokenImgUrl || ""}
                            onLoad={() => setShowImageLoading(false)}
                            alt="swords of blood early adopter token"
                            className={"token-img" + (showImageLoading ? " hide" : "")}
                        />
                    )}

                    {/* if token is bionicowls early adopter nft */}
                    {subdomain === "bionicowls" && (
                        <img
                            src={tokenImgUrl || ""}
                            onLoad={() => setShowImageLoading(false)}
                            alt="bionicowls early adopter token"
                            className={"token-img" + (showImageLoading ? " hide" : "")}
                        />
                    )}
                    {/* if token is zk companions mint pass */}
                    {subdomain === "bionicowls" && (
                        <img
                            src={tokenImgUrl || ""}
                            onLoad={() => setShowImageLoading(false)}
                            alt="zk companions mint pass"
                            className={"token-img" + (showImageLoading ? " hide" : "")}
                        />
                    )}
                </div>
                <div className="name-description-wrap">
                    <div className="token-name">{token.name || token.poolName || tokenMetadata?.name}</div>
                    <div className="token-chain-wrap">
                        {/* note polygon is for SOB early adopter token */}
                        <img
                            src={
                                networkIcons[
                                    subdomain === "zkc"
                                        ? "zksynctest"
                                        : subdomain === "bionicowls"
                                          ? "arbitrumnova"
                                          : subdomain === "swordsofblood"
                                            ? "polygon"
                                            : token.chain
                                ]
                            }
                            alt={token.chain}
                        />
                        <span>
                            {subdomain === "zkc"
                                ? "zksynctest"
                                : subdomain === "bionicowls"
                                  ? "arbitrumnova"
                                  : subdomain === "swordsofblood"
                                    ? "polygon"
                                    : token.chain}
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    )
}
